import React from 'react';
import PropTypes from 'prop-types';
import injectSheet from 'react-jss';
import cn from 'classnames';
import {
  responsiveImageType,
  linkType,
} from '../../propTypes';
import ResponsiveImage from '../ResponsiveImage';
import Link from '../Link';
import theme, { sliceFontStyle } from '../../styles/theme';
import get from '../../helpers/get';

const Tile = ({
  classes,
  title,
  heroImage,
  copy,
  moreInfo,
}) => {
  const hasTitle = !!(title && title.trim());

  return (
    <div className={cn(classes.tile, !hasTitle && classes.withoutTitle)}>
      <ResponsiveImage
        aspect={get(heroImage, ['aspect'])}
        className={get(classes, ['heroImage'])}
        images={get(heroImage, ['sizes'])}
        alt={get(heroImage, ['alt']) || 'Hero Image'}
        blur={get(heroImage, ['blur'])}
      />
      {hasTitle && (
        <h2>
          {title}
        </h2>
      )}
      <p>
        {copy}
      </p>
      {moreInfo && (
        <Link link={moreInfo} className={classes.link}>
          {moreInfo.text || 'Learn more'}
        </Link>
      )}
    </div>
  );
};

export const tileShape = {
  heroImage: responsiveImageType,
  title: PropTypes.string,
  copy: PropTypes.string,
  moreInfo: linkType,
};

Tile.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  ...tileShape,
  imageSize: PropTypes.number,
  titleAlignment: PropTypes.string,
  textAlignment: PropTypes.string,
  titleFontStyle: PropTypes.string,
  textFontStyle: PropTypes.string,
};

const tileDefaultProps = {
  title: undefined,
  heroImage: undefined,
  copy: undefined,
  moreInfo: undefined,
  imageSize: undefined,
  textAlignment: 'center',
  titleAlignment: 'center',
  titleFontStyle: sliceFontStyle['Bold (Proxima Nova Bold)'],
  textFontStyle: sliceFontStyle['Body (Proxima Nova Regular)'],
};

Tile.defaultProps = {
  ...tileDefaultProps,
};

export default injectSheet({
  tile: {
    color: theme.colors.brandBlack,
    textAlign: ({ textAlignment }) => textAlignment,
    display: 'flex',
    flexFlow: 'column wrap',
    [theme.breakpoints.down('md')]: {
      '&:not(:last-child)': {
        marginBottom: 16,
      },
    },
    '& > *': {
      width: '100%',
      flexGrow: 0,
    },
    '& h2': {
      whiteSpace: 'pre-wrap',
      margin: 0,
      padding: [16, 16, 0],
      fontFamily: ({ titleFontStyle }) => titleFontStyle,
      fontWeight: ({ titleFontStyle }) => (titleFontStyle === theme.fonts.heading ? 'bold' : 'normal'),
      fontSize: 16,
      textAlign: ({ titleAlignment }) => titleAlignment,
    },
    'div& p': {
      padding: [16, 16, 0],
      lineHeight: 1.5,
      flexGrow: 1,
      fontSize: 14,
      margin: 0,
      fontFamily: ({ textFontStyle }) => textFontStyle,
    },
  },
  withoutTitle: {
    'div& p': {
      paddingTop: 32,
    },
  },
  heroImage: {
    maxWidth: ({ imageSize }) => imageSize || 90,
  },
  link: {
    padding: [16, 16, 0],
    fontSize: 14,
    fontFamily: ({ textFontStyle }) => textFontStyle,
    color: theme.colors.brandBlack,
    textDecoration: 'underline',
    '&:hover, &:focus': {
      color: theme.colors.linkHover,
    },
  },
})(Tile);
